import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { makeStyles } from '@material-ui/core/styles';
import { Snackbar } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Close from '@material-ui/icons/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton, Typography, MenuItem, Menu } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Polygon, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Cookies from 'js-cookie';
import api from '../../../../../services/api';

// Configurações globais e Ícones
import globalConfig from '../../../../../configs/globalConfigs';
import icone_casa from '../../../../../assets/imgs/icone_casa.png';
import icone_loja from '../../../../../assets/imgs/icone_loja.png';
import icone_pgm from '../../../../../assets/imgs/icone_pgm.png';
import icone_medidor from '../../../../../assets/imgs/icone_medidor.png';
import icone_subestacao from '../../../../../assets/imgs/icone_subestacao.png';
import alarmeAudio from '../../../../../assets/mp3/alarme.mp3';

const useStyles = makeStyles((theme) => ({
    mapContainer: {
        width: '100%',
        height: '100%',
        position: 'relative',
        '& .leaflet-container': {
            width: '100%',
            height: '100%',
        }
    },
    perimeterInfo: {
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000,
        padding: theme.spacing(1),
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
    contextMenu: {
        zIndex: 2000,
    }
}));

// --- Gerenciador de Áudio ---
const audioManager = {
    audio: new Audio(alarmeAudio),
    isPlaying: false,
    play() {
        if (!this.isPlaying) {
            this.audio.loop = true;
            this.audio.volume = 1.0;
            this.audio.play().then(() => { this.isPlaying = true; }).catch(e => console.error("Erro áudio:", e));
        }
    },
    pause() {
        if (this.isPlaying) {
            this.audio.pause();
            this.audio.currentTime = 0;
            this.isPlaying = false;
        }
    }
};

// --- Sub-componentes Estáveis ---

const LeafletMapController = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        if (center && center.lat && center.lng) {
            map.setView([center.lat, center.lng], zoom || map.getZoom(), { animate: true });
        }
    }, [center, zoom, map]);
    return null;
};

const LeafletMapEvents = ({ onMapClick, onZoomChange }) => {
    useMapEvents({
        click: (e) => { if (onMapClick) onMapClick(e.latlng); },
        zoomend: (e) => { if (onZoomChange) onZoomChange(e.target.getZoom()); }
    });
    return null;
};

/**
 * Marcador de Equipamento (PGM) com lógica de alarme e comandos.
 */
const LeafletPGMMarker = ({ equipamento, onClick, onCommand }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleRightClick = (e) => {
        e.originalEvent.preventDefault();
        setAnchorEl(e.originalEvent.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleAction = (cmd) => {
        onCommand(cmd, equipamento);
        handleClose();
    };

    // Lógica de Status
    const now = new Date();
    const lastResponse = new Date(equipamento.modulo_pgm.data_hora_ultima_resposta);
    const diff = (now - lastResponse) / 1000 / 60;
    const alarme = equipamento.alarme;
    const armada = equipamento.modulo_pgm.armada;

    let color = "gray";
    let isBlinking = false;

    if (alarme) { color = "red"; isBlinking = true; }
    else if (diff < 2) { color = armada ? "green" : "yellow"; }
    else { color = "red"; isBlinking = true; }

    const icon = L.divIcon({
        className: 'pgm-icon',
        html: `<div style="
            background-image: url(${icone_pgm}); 
            background-size: cover; 
            width: 30px; height: 30px; 
            border: 3px solid ${color}; 
            border-radius: 50%;
            animation: ${isBlinking ? 'blink 1s infinite' : 'none'};
            background-color: white;
        "></div>
        <style>@keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }</style>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18]
    });

    return (
        <>
            <Marker
                position={[parseFloat(equipamento.latitude), parseFloat(equipamento.longitude)]}
                icon={icon}
                eventHandlers={{
                    click: () => onClick(parseFloat(equipamento.latitude), parseFloat(equipamento.longitude)),
                    contextmenu: handleRightClick
                }}
            />
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={() => handleAction(1)}>Armar</MenuItem>
                <MenuItem onClick={() => handleAction(4)}>Desarmar</MenuItem>
                <MenuItem onClick={() => handleAction(2)}>Ativar Rele 2</MenuItem>
                <MenuItem onClick={() => handleAction(5)}>Desativar Rele 2</MenuItem>
            </Menu>
        </>
    );
};

// --- Componente Principal ---
export default function MapaDoLocal(props) {
    const { instalacao, darkMode, tipo_icone, nome_mapa } = props;
    const classes = useStyles();

    const [mapCenter, setMapCenter] = useState(
        instalacao ? { lat: parseFloat(instalacao.latitude), lng: parseFloat(instalacao.longitude) } : null
    );
    const [zoom, setZoom] = useState(18);
    const [definingPerimeter, setDefiningPerimeter] = useState(false);
    const [perimeterPoints, setPerimeterPoints] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Menu de Contexto da Instalação
    const [anchorElInst, setAnchorElInst] = useState(null);

    useEffect(() => {
        if (instalacao) {
            setMapCenter({ lat: parseFloat(instalacao.latitude), lng: parseFloat(instalacao.longitude) });
            // Alarme Sonoro
            const hasAlarme = instalacao.equipamentos?.some(e => e.alarme);
            if (hasAlarme) audioManager.play(); else audioManager.pause();
        }
    }, [instalacao]);

    const getInstalacaoIcon = useMemo(() => {
        let iconUrl = icone_casa;
        if (tipo_icone === 1) iconUrl = icone_loja;
        if (tipo_icone === 2) iconUrl = icone_subestacao;

        return L.divIcon({
            className: 'custom-inst-icon',
            html: `<div style="display: flex; flex-direction: column; align-items: center;">
                <img src="${iconUrl}" style="width: 40px; height: 40px;" />
                <span style="color: white; font-size: 10px; font-weight: bold; text-shadow: 1px 1px 2px black;">${nome_mapa || ''}</span>
            </div>`,
            iconSize: [60, 60],
            iconAnchor: [30, 20]
        });
    }, [tipo_icone, nome_mapa]);

    const handleMapClick = (latlng) => {
        if (definingPerimeter) {
            if (perimeterPoints.length < 8) {
                setPerimeterPoints(prev => [...prev, [latlng.lat, latlng.lng]]);
            }
        }
    };

    const handleCommand = async (comando, eq) => {
        try {
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            const url = `/v1/protected/modulopgm/tarefas/criarporareatecnica/${Cookies.get("id_usuario")}/${eq.modulo_pgm.id_modulo_pgm}/${comando}`;
            await api.get(url, { headers });
            setSnackbar({ open: true, message: 'Comando enviado!', severity: 'success' });
        } catch (e) {
            setSnackbar({ open: true, message: 'Erro ao enviar comando.', severity: 'error' });
        }
    };

    const salvarPerimetro = async () => {
        try {
            const cadastro = {};
            perimeterPoints.forEach((p, i) => {
                cadastro[`latitude${i + 1}`] = p[0].toString();
                cadastro[`longitude${i + 1}`] = p[1].toString();
            });
            const headers = { 'Authorization': 'Bearer ' + Cookies.get("token") };
            await api.put(`/v1/protected/clientes/instalacoes/atualizarperimetro/${instalacao.id_instalacao}`, cadastro, { headers });
            setSnackbar({ open: true, message: 'Perímetro salvo!', severity: 'success' });
            setDefiningPerimeter(false);
        } catch (e) {
            setSnackbar({ open: true, message: 'Erro ao salvar perímetro.', severity: 'error' });
        }
    };

    // Tiles
    const satelliteUrl = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
    const darkUrl = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
    const lightUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

    const tileUrl = darkMode ? darkUrl : satelliteUrl;

    if (!mapCenter) return <Typography>Carregando...</Typography>;

    const perimetroExistente = instalacao.perimetro ? Array.from({ length: 8 }, (_, i) => {
        const lat = parseFloat(instalacao.perimetro[`latitude${i + 1}`]);
        const lng = parseFloat(instalacao.perimetro[`longitude${i + 1}`]);
        return !isNaN(lat) && !isNaN(lng) ? [lat, lng] : null;
    }).filter(p => p) : [];

    return (
        <div className={classes.mapContainer}>
            <MapContainer center={[mapCenter.lat, mapCenter.lng]} zoom={zoom} zoomControl={false}>
                <TileLayer url={tileUrl} attribution="Esri / OSM" />
                <LeafletMapController center={mapCenter} zoom={zoom} />
                <LeafletMapEvents onMapClick={handleMapClick} onZoomChange={setZoom} />

                {/* Marcador Principal */}
                <Marker
                    position={[mapCenter.lat, mapCenter.lng]}
                    icon={getInstalacaoIcon}
                    eventHandlers={{
                        contextmenu: (e) => { e.originalEvent.preventDefault(); setAnchorElInst(e.originalEvent.currentTarget); }
                    }}
                />
                <Menu anchorEl={anchorElInst} open={Boolean(anchorElInst)} onClose={() => setAnchorElInst(null)}>
                    <MenuItem onClick={() => { window.open(`/gestao/administracao/clientes/gerenciarcliente/instalacao/gerenciarinstalacao/${instalacao.id_instalacao}/0`, '_blank'); setAnchorElInst(null); }}>Gerenciar</MenuItem>
                    {!definingPerimeter && <MenuItem onClick={() => { setDefiningPerimeter(true); setPerimeterPoints([]); setAnchorElInst(null); }}>Definir Perímetro</MenuItem>}
                </Menu>

                {/* Equipamentos */}
                {instalacao.equipamentos?.map(eq => (
                    eq.latitude && eq.longitude && eq.tipo_equipamento_area_tecnica === 0 && (
                        <LeafletPGMMarker key={eq.id_equipamento} equipamento={eq} onClick={(la, lo) => setMapCenter({ lat: la, lng: lo })} onCommand={handleCommand} />
                    )
                ))}

                {/* Perímetros */}
                {definingPerimeter ? (
                    <>
                        {perimeterPoints.length === 2 && <Polyline positions={perimeterPoints} color="red" />}
                        {perimeterPoints.length > 2 && <Polygon positions={perimeterPoints} color="red" fillOpacity={0.4} />}
                        {perimeterPoints.map((p, i) => (
                            <Marker key={i} position={p} icon={L.divIcon({ className: 'dot', html: '<div style="background:red; width:8px; height:8px; border-radius:50%;"></div>', iconSize: [8, 8] })} />
                        ))}
                    </>
                ) : (
                    perimetroExistente.length > 2 && <Polygon positions={perimetroExistente} color="blue" fillOpacity={0.2} />
                )}
            </MapContainer>

            {/* UI de Perímetro */}
            {definingPerimeter && (
                <Paper className={classes.perimeterInfo}>
                    <Typography variant="caption">Pontos: {perimeterPoints.length}/8</Typography>
                    <div style={{ display: 'flex', gap: 5 }}>
                        <IconButton size="small" color="primary" onClick={salvarPerimetro}><SettingsIcon /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setDefiningPerimeter(false)}><Close /></IconButton>
                    </div>
                </Paper>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />
        </div>
    );
}
